import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} [wrapper]
 * @property {HTMLElement} [content]
 * @property {HTMLElement[]} [slides]
 */

/**
 * Aromely Announcement Bar - supports multiple animation modes
 * @extends {Component<Refs>}
 */
class AromelyAnnouncementBar extends Component {
  /** @type {number | undefined} */
  #interval = undefined;

  /** @type {number} */
  #current = 0;

  /** @type {boolean} */
  #isPaused = false;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion()) return;

    const mode = this.getAttribute('mode');
    const autoplay = this.hasAttribute('autoplay');

    switch (mode) {
      case 'marquee':
        this.#initMarquee();
        break;
      case 'fade':
      case 'slide-down':
        if (autoplay) {
          this.#initRotation();
        }
        break;
    }

    document.addEventListener('visibilitychange', this.#handleVisibility);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopRotation();
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  /**
   * Initialize marquee mode by cloning content for seamless loop
   */
  #initMarquee() {
    const { wrapper, content } = this.refs;

    if (!wrapper || !content) return;

    // Calculate how many clones we need to fill at least 2x the viewport width
    const contentWidth = content.offsetWidth;
    const viewportWidth = window.innerWidth;
    const clonesNeeded = Math.max(1, Math.ceil((viewportWidth * 2) / contentWidth));

    // Create enough clones to ensure seamless looping
    for (let i = 0; i < clonesNeeded; i++) {
      const clone = content.cloneNode(true);

      if (clone instanceof HTMLElement) {
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('ref');
        wrapper.appendChild(clone);
      }
    }

    // Set the translate distance to the original content width
    // This ensures the animation loops seamlessly
    wrapper.style.setProperty('--marquee-translate', `-${contentWidth}px`);

    this.addEventListener('pointerenter', this.#pauseMarquee);
    this.addEventListener('pointerleave', this.#resumeMarquee);
  }

  /**
   * Initialize rotation for fade and slide-down modes
   */
  #initRotation() {
    const interval = (parseInt(this.getAttribute('interval') || '', 10) || 5) * 1000;
    this.#interval = window.setInterval(() => this.#next(), interval);
  }

  /**
   * Advance to the next slide
   */
  #next() {
    const { slides } = this.refs;

    if (!slides?.length || slides.length <= 1) return;

    const prev = this.#current;
    this.#current = (this.#current + 1) % slides.length;

    const mode = this.getAttribute('mode');

    // For slide-down mode, add exiting class for proper exit animation
    if (mode === 'slide-down') {
      slides[prev].classList.add('aromely-announcement-bar__slide--exiting');
      slides[prev].classList.remove('aromely-announcement-bar__slide--active');

      // Remove exiting class after animation completes
      setTimeout(() => {
        slides[prev].classList.remove('aromely-announcement-bar__slide--exiting');
      }, 500);
    } else {
      slides[prev].classList.remove('aromely-announcement-bar__slide--active');
    }

    slides[this.#current].classList.add('aromely-announcement-bar__slide--active');
  }

  /**
   * Stop rotation interval
   */
  #stopRotation() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  /**
   * Handle visibility change - pause when hidden, resume when visible
   */
  #handleVisibility = () => {
    const autoplay = this.hasAttribute('autoplay');
    if (!autoplay) return;

    if (document.hidden) {
      this.#stopRotation();
    } else {
      const mode = this.getAttribute('mode');
      if (mode === 'fade' || mode === 'slide-down') {
        this.#initRotation();
      }
    }
  };

  /**
   * Pause marquee animation
   */
  #pauseMarquee = () => {
    this.#isPaused = true;
    this.refs.wrapper?.classList.add('aromely-announcement-bar__wrapper--paused');
  };

  /**
   * Resume marquee animation
   */
  #resumeMarquee = () => {
    this.#isPaused = false;
    this.refs.wrapper?.classList.remove('aromely-announcement-bar__wrapper--paused');
  };
}

if (!customElements.get('aromely-announcement-bar')) {
  customElements.define('aromely-announcement-bar', AromelyAnnouncementBar);
}
